import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react'
import Mapbox, { GeolocateControl, MapLayerMouseEvent, NavigationControl } from 'react-map-gl'
import { Typography } from '@material-tailwind/react'
import { useXp } from '../contexts/XpContext'
import { MAPBOX_TOKEN } from '../utils/constants/mapbox.constants'
import HeaderSpace from '../layout/HeaderSpace'
import Controls from './Controls'
import ManualModeInstructions from './ManualModeInstructions'
import { useMapControls } from '../contexts/MapControlsContext'
import ManualLocationMarker from './ManualLocationMarker'
import { useDevice } from '../hooks/device'
import MapStyleSelector from '../components/MapStyleSelector'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import Buildings3DLayer from './Buildings3DLayer'
import TerrainSourceLayer from './TerrainSourceLayer'
import XpSpotsCluster from './XpSpotsCluster'
import { getBoundsFromCoords } from '../utils/helpers/map.helpers'
import { FIT_BOUNDS_ANIMATION_DURATION, FIT_BOUNDS_PADDING } from '../utils/constants/map.constants'
import { useScreenBreakpoint } from '../hooks/toolkit'
import { PatternChip } from '../components/PatternChip'
import { Position } from '../types/geoXp'

const MapboxMap = () => {
  const { manualMode, updateLocation, positions, patterns, active, hasManualSpots, details } = useXp()
  const { mapRef, setRouteSegment } = useMapControls()
  const device = useDevice()
  const platformType = device?.platformType
  const breakpoint = useScreenBreakpoint()

  const isMobile = platformType === 'mobile'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const geoControlRef = useRef<any>(null)

  useEffect(() => {
    if (geoControlRef.current) {
      setTimeout(() => geoControlRef.current?.trigger(), 1000)
    }
  }, [geoControlRef.current])

  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (manualMode) {
        const { lng, lat } = event.lngLat
        updateLocation({ latitude: lat, longitude: lng })
      }
    },
    [manualMode, updateLocation]
  )

  const { mapStyleUrl, apply3DMap } = useUserPreferences()

  const advMapsAuth = useFeatureAuth(FEATURES.ADVANCED_MAP_STYLES)

  const [selectedPatterns, setSelectedPatterns] = useState<Position[]>([])

  const fitPatternBounds = useCallback((pattern: Position) => {
    if (!pattern || !mapRef.current) return

    const coords = pattern.positions.map((position) => [position.lon, position.lat]) as [number, number][]
    const mapBounds = getBoundsFromCoords(coords)
    mapRef.current.fitBounds(mapBounds, {
      padding: FIT_BOUNDS_PADDING,
      duration: FIT_BOUNDS_ANIMATION_DURATION,
    })
  }, [])

  const viewPattern = useCallback((pattern: Position, fitBounds = false, hideOthers = false) => {
    if (hideOthers) {
      setSelectedPatterns([pattern])
    } else {
      setSelectedPatterns((prev) => [...prev.filter((p) => p.id !== pattern.id), pattern])
    }
    if (fitBounds) fitPatternBounds(pattern)
  }, [])

  const hidePattern = useCallback((pattern: Position) => {
    setSelectedPatterns((prev) => [...prev.filter((p) => p.id !== pattern.id)])
  }, [])

  const togglePattern = useCallback(
    (pattern: Position, fitBounds = false, hideOthers = false) => {
      if (!selectedPatterns.map((p) => p.id).includes(pattern.id)) {
        viewPattern(pattern, fitBounds, hideOthers)
      } else {
        hidePattern(pattern)
      }
    },
    [selectedPatterns]
  )

  const activePositionsIds = useMemo(
    () =>
      active
        ?.map((audio) => audio?.spot?.position)
        .filter((posId) => posId)
        .map((pos) => pos as string),
    [active]
  )

  // auto select pattern if spot is active
  useEffect(() => {
    const activePattern = positions.find((pattern) =>
      activePositionsIds.some((activeId) => pattern.positions.map((pos) => pos.id).includes(activeId))
    )
    if (activePattern) viewPattern(activePattern)
  }, [activePositionsIds])

  // select all patterns on mount
  useEffect(() => {
    setSelectedPatterns(positions)
  }, [])

  return (
    <div className="h-[calc(100%_-_4.5rem)] w-full relative" data-testid="experience-map">
      <HeaderSpace />

      <Mapbox
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={mapStyleUrl}
        attributionControl={false}
        logoPosition="top-right"
        style={{
          height: '100%',
          width: '100%',
        }}
        initialViewState={{
          longitude: 7.675806773171514,
          latitude: 45.06451515609597,
          zoom: 4,
        }}
        onClick={handleMapClick}
        terrain={{
          source: 'mapbox-dem',
          exaggeration: apply3DMap ? 1 : 0,
        }}
        minZoom={2}
      >
        {!!details?.name && (
          <div className="absolute p-3 left-[50%] z-[10] -translate-x-1/2 top-6">
            <Typography
              variant="h3"
              className="font-bold text-primary dark:text-white"
              data-testid="experience-map-title"
            >
              {details.name}
            </Typography>
          </div>
        )}
        {manualMode && (
          <>
            <ManualModeInstructions />
            <ManualLocationMarker />
          </>
        )}

        {advMapsAuth && (
          <>
            <div
              className="absolute p-3 left-1"
              style={{
                bottom:
                  (patterns.length > 1 ? 54 : 0) +
                  (breakpoint === 'sm' && (active.length || hasManualSpots) ? 92 : 0) +
                  16,
              }}
            >
              <MapStyleSelector />
            </div>
            <Buildings3DLayer />
            <TerrainSourceLayer />
          </>
        )}

        {!!patterns && patterns.length > 1 && (
          <div
            className={`
              absolute py-3 px-4
              flex gap-2 items-center overflow-x-auto
              w-full
            `}
            style={{
              bottom: breakpoint === 'sm' && (active.length || hasManualSpots) ? 110 : 0,
              paddingBottom: 16,
            }}
          >
            {patterns.map((pattern) => (
              <PatternChip
                pattern={pattern}
                selectedPatterns={selectedPatterns}
                viewPattern={viewPattern}
                togglePattern={togglePattern}
              />
            ))}
          </div>
        )}

        <Controls />

        <NavigationControl position="top-right" />

        {!manualMode && (
          <GeolocateControl
            ref={geoControlRef}
            position="top-right"
            trackUserLocation={isMobile}
            showUserHeading={isMobile}
            positionOptions={{
              enableHighAccuracy: true,
            }}
            onGeolocate={(location) => {
              setRouteSegment({
                longitude: location.coords.longitude,
                latitude: location.coords.latitude,
              })
            }}
          />
        )}

        <XpSpotsCluster zoomOnClick selectedPatterns={selectedPatterns} />
      </Mapbox>
    </div>
  )
}

export default MapboxMap
